<script>
import { AisInstantSearch } from 'vue-instantsearch/vue3/es';
import { algoliasearch } from 'algoliasearch';
import { history } from 'instantsearch.js/es/lib/routers';
import Results from './AlgoliaResults.vue';
import AlgoliaSearchComponent from './AlgoliaSearch.vue';

const getValidPage = (page) => {
  const parsedPage = Number.parseInt(page, 10);
  if (!Number.isInteger(parsedPage) || parsedPage < 0) {
    return 0;
  }
  return parsedPage;
};

export default {
  name: 'Aloglia',

  components: {
    results: Results,
    AlgoliaSearchComponent,
    AisInstantSearch,
  },

  props: {
    locale: {
      type: String,
      default: 'en-US',
    },

    algoliaCreds: {
      type: Object,
      default: () => {},
    },

    content: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      routing: {
        results: {},
        router: history(),
        stateMapping: {
          stateToRoute(uiState) {
            const indexName = Object.keys(uiState)[0];
            const indexState = uiState[indexName] || {};
            return { query: indexState.query, page: indexState.page };
          },
          routeToState() {
            // Will be set in created() hook
            return {};
          },
        },
      },
      indexName: '',
    };
  },

  computed: {
    location() {
      return window.location;
    },

    searchClient() {
      const algoliaClient = algoliasearch(
        this.algoliaCreds.APP_ID,
        this.algoliaCreds.API_KEY,
      );

      return {
        ...algoliaClient,
        async search(requests) {
          const normalizedRequests = requests.map((request) => {
            const normalizedParams = { ...request.params };
            if (normalizedParams.page !== undefined) {
              normalizedParams.page = getValidPage(normalizedParams.page);
            }
            return {
              ...request,
              params: normalizedParams,
            };
          });

          const { query, ruleContexts, page } = normalizedRequests[0].params;
          // Avoid queries to Algolia when the query is empty
          // return custom results object
          if (!query || !ruleContexts) {
            return Promise.resolve({
              results: normalizedRequests.map(() => ({
                hits: [],
                page: 0,
                hitsPerPage: 0,
                nbHits: 0,
                nbPages: 0,
                query: '',
                params: '',
              })),
            });
          }
          // If data results object hasn't been set, if the request query is different from
          // the current query and the request has a page param
          // go fetch the algolia data with the changed parameters, otherwise return the initial result object.
          if (!this.results || (this.results?.results[0]?.query !== query || page !== undefined)) {
            this.results = await algoliaClient.search(normalizedRequests);

            // set results data to avoid multiple requests for the same query
            return this.results;
          }
          return this.results;
        },
      };
    },

    initialUiState() {
      const query = new URL(window.location.href).searchParams.get('query');
      const page = new URL(window.location.href).searchParams.get('page');
      const initialState = {};

      if (query) {
        initialState.query = query;
      }
      if (page) {
        initialState.page = getValidPage(page);
      }

      return {
        [this.algoliaCreds.INDEX_NAME]: initialState,
      };
    },
  },

  created() {
    this.indexName = this.algoliaCreds.INDEX_NAME;
    this.routing.stateMapping.routeToState = (routeState) => ({
      [this.indexName]: {
        query: routeState.query,
        page: getValidPage(routeState.page),
      },
    });
  },

  mounted() {
    const { APP_ID } = this.algoliaCreds;
    const { API_KEY } = this.algoliaCreds;

    window.aa('init', {
      appId: APP_ID,
      apiKey: API_KEY,
    });
  },
};
</script>

<template>
  <ais-instant-search
    :search-client="searchClient"
    :index-name="algoliaCreds.INDEX_NAME"
    :routing="routing"
    :insights="true"
    :initial-ui-state="initialUiState"
  >
    <AlgoliaSearchComponent :content="content.references[0].fields" />
    <results
      :locale="locale"
      :content="content.references[0].fields"
    />
  </ais-instant-search>
</template>
